import React, { memo, useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'
import { t } from 'i18next'
import { Heart, X } from 'lucide-react'

import { useMember, useSession, useTenant } from '@rewardops/auth'
import { useLocale } from '@rewardops/ui'
import { AnalyticEvents, type ResponseItem } from '@rewardops/utils'

import { getDeviceType } from '~/utils/device'
import { useAnalytics } from '~/components/hooks/use-analytics'
import { useMemberUUID } from '~/components/hooks/use-member-uuid'
import { useFavouritingWishlisting } from '../../../hooks/use-favouriting-wishlisting'

type FavouritesWishlistHeartProps = {
  item: ResponseItem
}

const FavouritesWishlistHeart: React.FC<FavouritesWishlistHeartProps> = memo(
  ({ item }: FavouritesWishlistHeartProps) => {
    const tenant = useTenant()

    const pathname = usePathname()
    const { locale } = useLocale()
    const analytics = useAnalytics()
    const member = useMember()
    const memberUUID = useMemberUUID()

    if (!tenant.flags.wishlist) {
      return null
    }

    const {
      savedItemIds,
      heartIconsInProgress,
      addToFavouriteOrWishlist,
      removeFromFavouriteOrWishlist,
    } = useFavouritingWishlisting()

    const supplierItem = item?.supplier_items?.[0]
    const supplierItemId = Number(supplierItem?.id)

    const isFavouritedOrWishlisted = savedItemIds?.has(supplierItemId)
    const isInProgress = heartIconsInProgress?.has(supplierItemId)

    const ariaMessageId = `itemDetail.${
      isFavouritedOrWishlisted ? 'removeFrom' : 'addTo'
    }Wishlist`

    const ariaMessage = () => {
      return t(ariaMessageId)
    }

    const [isFilled, setIsFilled] = useState(isFavouritedOrWishlisted)
    const [addedOrRemoved, setAddedOrRemoved] = useState(false)

    const { data: session } = useSession()

    useEffect(() => {
      setIsFilled(isFavouritedOrWishlisted)
    }, [isFavouritedOrWishlisted])

    const onClick = () => {
      if (!session?.user?.member) {
        if (tenant?.loginLink?.[locale]) {
          window.location.href = tenant.loginLink[locale]
        } else {
          return fetch(
            `/api/auth/login?returnUrl=${encodeURIComponent(
              window?.location?.href,
            )}&locale=${locale}`,
          )
            .then((response) => response.json())
            .then((response) => {
              window.location.href = response.authRedirect?.url
            })
        }
      }

      const caller = isFavouritedOrWishlisted
        ? removeFromFavouriteOrWishlist
        : addToFavouriteOrWishlist

      setAddedOrRemoved(true)

      const analyticsData = {
        currency: tenant?.defaultCurrency,
        item_id: item?.id,
        item_name: item?.name,
        item_type: item?.type,
        user_status: !!session ? 'signed_in' : 'guest',
        list_name: pathname?.split('/').pop() ?? '',
        success: true,
        action: isFavouritedOrWishlisted ? 'remove' : 'add',
        items: [
          {
            id: item?.id,
            name: item?.name,
            price:
              item?.supplier_items?.[0]?.prices?.sale_price?.[
                tenant?.defaultPoints
              ] ??
              item?.supplier_items?.[0]?.prices?.regular_price?.[
                tenant?.defaultPoints
              ] ??
              0,
          },
        ],
        member_id: memberUUID,
        device_type: getDeviceType(),
      }

      caller({ item })
        .catch(() => {
          analytics.sendCustomGoogleAnalyticsEvent({
            type: isFavouritedOrWishlisted
              ? AnalyticEvents.RemoveFromWishlist
              : AnalyticEvents.AddToWishlist,
            data: {
              ...analyticsData,
              success: false,
            },
          } as any)
        })
        .finally(() => {
          analytics.sendCustomGoogleAnalyticsEvent({
            type: isFavouritedOrWishlisted
              ? AnalyticEvents.RemoveFromWishlist
              : AnalyticEvents.AddToWishlist,
            data: analyticsData,
          } as any)
        })
    }

    useEffect(() => {
      setIsFilled(isFavouritedOrWishlisted)
    }, [isFavouritedOrWishlisted])

    const onMouseEnter = () => {
      setIsFilled(!isFilled)
      setAddedOrRemoved(false)
    }

    const onMouseLeave = () => {
      if (!addedOrRemoved) {
        setIsFilled(!isFilled)
      }
    }

    let Icon = <Heart fill={isFilled ? 'currentColor' : 'none'} />

    if (pathname?.split(locale)?.[1] === '/wishlist') {
      Icon = <X />
    }

    return (
      <button
        className={`z-10 p-2.5 ${isInProgress ? 'text-gray-300' : ''}`}
        onClick={onClick}
        disabled={isInProgress}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        aria-label={ariaMessage()}
        id="favourites-wishlist-heart"
      >
        {Icon}
      </button>
    )
  },
)

export { FavouritesWishlistHeart }
